import { useEffect, useState } from 'react';
import { usePhone } from '@/hooks/usePhone';
import { INBOUND_CHANNEL_LABEL, markInboundRead, sortInboundUnreadFirst, type InboundMessageView } from '@/lib/people-data';
import { when } from '@/lib/format';
import s from '@/pages/console/console.module.css';

/**
 * "They wrote": what one person sent us through GHL (pv-auth #336), in full,
 * unread first and then newest first, so the page leads with what is waiting
 * for Brian. Each unread row has Mark read (a human looked; not a reply) and
 * every row has Reply, which opens the one composer with the message set as
 * the reply target. The GHL link opens the thread.
 *
 * Above 640px the rows are a table; under it one card per message, the
 * People card pattern. Mark read updates the row in place from the server's
 * answer, so nothing is claimed read that the API did not confirm.
 */
export function TheyWrote({ userId, messages, onReply, onRead }: {
  userId: string;
  messages: InboundMessageView[];
  onReply: (m: InboundMessageView) => void;
  /** After the server confirmed a read, so the caller can refresh counts. */
  onRead?: (m: InboundMessageView) => void;
}) {
  const phone = usePhone();
  const [rows, setRows] = useState<InboundMessageView[]>(() => sortInboundUnreadFirst(messages));
  useEffect(() => { setRows(sortInboundUnreadFirst(messages)); }, [messages]);
  const [busy, setBusy] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  const read = async (m: InboundMessageView) => {
    setBusy(m.id);
    setError(null);
    try {
      const updated = await markInboundRead(userId, m.id);
      setRows((cur) => sortInboundUnreadFirst(cur.map((r) => (r.id === updated.id ? updated : r))));
      onRead?.(updated);
    } catch (err) {
      setError(`Could not mark it read: ${(err as Error).message}`);
    } finally {
      setBusy(null);
    }
  };

  if (rows.length === 0) return <div className={s.empty}>Nothing written to us yet.</div>;

  const readWords = (m: InboundMessageView) => (m.readAt ? `read ${when(m.readAt)}${m.readBy ? ` by ${m.readBy}` : ''}` : 'unread');
  const actions = (m: InboundMessageView) => (
    <div className={s.msgActions}>
      {!m.readAt && <button type="button" className={s.btnGhost} disabled={busy === m.id} onClick={() => void read(m)} data-action="mark-read">{busy === m.id ? 'Marking' : 'Mark read'}</button>}
      <button type="button" className={s.btn} onClick={() => onReply(m)} data-action="reply">Reply</button>
      {m.ghlUrl ? <a className={s.linkA} href={m.ghlUrl} target="_blank" rel="noreferrer">Thread in GHL</a> : <span className={s.missing}>no GHL link</span>}
    </div>
  );

  return (
    <div className={s.stack}>
      {error && <div className={s.error}>{error}</div>}
      {phone ? (
        <div className={s.msgRows} data-panel="inbound-cards">
          {rows.map((m) => (
            <article key={m.id} className={`${s.msgRow} ${m.readAt ? '' : s.msgRowUnread}`} data-message={m.id} data-unread={m.readAt ? '0' : '1'}>
              <div className={s.msgRowHead}>
                <span className={`${s.pill} ${m.readAt ? '' : s.pill_unread}`}>{m.readAt ? 'read' : 'unread'}</span>
                <span className={s.jKind}>{INBOUND_CHANNEL_LABEL[m.channel]}</span>
                <span className={s.jTime}>{when(m.receivedAt)}</span>
              </div>
              {m.subject && <div className={s.msgSubject}>{m.subject}</div>}
              <div className={s.msgText}>{m.body}</div>
              <div className={s.sub}>{readWords(m)}</div>
              {actions(m)}
            </article>
          ))}
        </div>
      ) : (
        <div className={s.tableWrap} data-panel="inbound-table">
          <table className={s.table}>
            <thead><tr><th>When</th><th>Channel</th><th>What they wrote</th><th>Read</th><th></th></tr></thead>
            <tbody>
              {rows.map((m) => (
                <tr key={m.id} className={m.readAt ? '' : s.rowUnread} data-message={m.id} data-unread={m.readAt ? '0' : '1'}>
                  <td>{when(m.receivedAt)}</td>
                  <td><span className={s.jKind}>{INBOUND_CHANNEL_LABEL[m.channel]}</span></td>
                  <td className={s.msgCell}>
                    {m.subject && <div className={s.msgSubject}>{m.subject}</div>}
                    <div className={s.msgText}>{m.body}</div>
                  </td>
                  <td>{m.readAt ? <span className={s.sub}>{readWords(m)}</span> : <span className={`${s.pill} ${s.pill_unread}`}>unread</span>}</td>
                  <td>{actions(m)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
